/*
  Copyright Epic Games, Inc. All Rights Reserved.
*/

"use client";

import { SearchInput } from "@eds/react";
import { css } from "@panda/css";
import { useCallback, useEffect, useRef, useState } from "react";

const wrapperStyles = css({
	width: "full",
});

export type DirectorySearchInputProps = {
	value: string;
	onChange: (value: string) => void;
	placeholder?: string;
	/** aria-label for the native clear button. EDS requires it explicitly. Defaults to "Clear search". */
	clearLabel?: string;
	debounceMs?: number;
};

/**
 * Debounced search input. Debounce lives in the element (not the hook) so the pending
 * timer is scoped to the input's lifetime — `useEffect` cleanup cancels any in-flight
 * update on unmount, preventing orphaned fires that would clobber state after the user
 * has clicked a filter chip mid-typing.
 */
export function DirectorySearchInput({
	value,
	onChange,
	placeholder,
	clearLabel = "Clear search",
	debounceMs = 250,
}: DirectorySearchInputProps): React.JSX.Element {
	const [localValue, setLocalValue] = useState(value);
	const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

	// Sync local state when the controlled value changes externally (e.g. clearAllFilters).
	useEffect(() => {
		setLocalValue(value);
	}, [value]);

	useEffect(() => {
		return () => {
			if (timeoutRef.current) clearTimeout(timeoutRef.current);
		};
	}, []);

	const handleChange = useCallback(
		(next: string) => {
			setLocalValue(next);
			if (timeoutRef.current) clearTimeout(timeoutRef.current);
			timeoutRef.current = setTimeout(() => onChange(next), debounceMs);
		},
		[onChange, debounceMs],
	);

	return (
		<div className={wrapperStyles}>
			<SearchInput
				label={placeholder ?? "Search"}
				clearLabel={clearLabel}
				value={localValue}
				onChange={handleChange}
				onClear={() => {
					if (timeoutRef.current) clearTimeout(timeoutRef.current);
					setLocalValue("");
					onChange("");
				}}
			/>
		</div>
	);
}
